import { Link } from 'react-router-dom';
import { css } from '@emotion/react';
import { CheckCircle, List, Plus } from '@phosphor-icons/react';
import type { OnLog } from 'client/state/LogState';
import { Button } from 'client/components/Button';
import { Stack } from 'client/components/Stack';
import { containerStateToStatusIconProps, containerStateToTagProps, containerStateToText } from 'client/components/Status/statusUtils';
import { StatusTreeTagIcon } from 'client/components/StatusTree/components/StatusTreeTagIcon';
import { Tag } from 'client/components/Tag';
import { Tooltip } from 'client/components/Tooltip';
import { Typography } from 'client/components/Typography';
import { SshWarningDropdown } from '../SshWarningDropdown';
import type { ComponentStatusTree } from './componentsStatusUtils';
import { useContainerActions } from './useContainerActions';

interface ComponentSubContainerProps {
  deploymentID: number | null | undefined;
  deploymentUrl: string;
  namespace: string;
  pod: string;
  item:
    | ComponentStatusTree['containers'][number]['containers'][number]
    | ComponentStatusTree['containers'][number]['initContainers'][number];
  onLog?: OnLog;
}

export const ComponentSubContainer = ({ deploymentID, deploymentUrl, namespace, pod, item, onLog }: ComponentSubContainerProps) => {
  const { logPath, copyTooltip, handleLog, handleSsh, isSshLoading } = useContainerActions({
    deploymentID,
    deploymentUrl,
    namespace,
    pod,
    container: item.name,
    onLog,
  });

  return (
    <Stack component="li" gap={6} horizontal css={stackStyle}>
      <Tag noWrap {...containerStateToTagProps[item.state]} css={tagStyle}>
        <StatusTreeTagIcon
          {...containerStateToStatusIconProps[item.state]}
          icon={item.state === 'terminated' ? <CheckCircle weight="bold" /> : undefined}
        />
        {item.name}
      </Tag>
      <Typography variant="body2">{containerStateToText[item.state]}</Typography>
      <span style={{ flex: 1 }} />
      <Link to={logPath}>
        <Button tiny icon={<List weight="bold" />} onClick={handleLog}>
          Logs
        </Button>
      </Link>
      {item.state === 'running' && (
        <Tooltip open={!!copyTooltip} title={copyTooltip} side="right" align="start">
          <div>
            <SshWarningDropdown item="container">
              <Button tiny icon={<Plus weight="bold" />} loading={isSshLoading} onClick={handleSsh}>
                Connect
              </Button>
            </SshWarningDropdown>
          </div>
        </Tooltip>
      )}
    </Stack>
  );
};

const stackStyle = css`
  align-items: center;

  > p.body2 {
    opacity: 0.6;
  }

  a {
    text-decoration: none;
  }
`;

const tagStyle = css`
  display: flex;
  align-items: center;
  gap: 4px;
`;
